import { getMediaViews, getMediaPlayback as getMediaPlaybackApi, getMediaPlaybackTotals as getMediaPlaybackTotalsApi } from '../assets/libs/broox/brooxCloudApi';
import { AccountParams } from './AccountParams';
import { Collapsed, findById } from './Collapsed';
import { Filter, FilterService } from './Filter';
import { cachedRequest, requestKey } from './RequestCache';

interface Media {
  id: string,
  name: string,
  campaign: string,
  playlist: string,
  noViews: number,
  fullViews: number,
  partialViews: number
}

interface MediaPlaybackAggregate {
  noViews: number,
  fullViews: number,
  partialViews: number,
  mostViewedCampaign: { name: string, count: number },
  list: Media[]
}

interface MediaPlayback {
  mediaId: string,
  mediaName: string,
  playlistId: string,
  playlistName: string,
  campaignId: string,
  campaignName: string,
  played: number,
  fully: number,
  partially: number
}

interface MediaPlaybackList {
  list: MediaPlayback[],
  more: number
}

interface Views {
  id: string,
  name: string,
  count: number
}

interface MediaPlaybackTotals {
  fullViews: number,
  partialViews: number,
  mostViewedCampaign: Views
}

const getMediaPlayback = async (filter: Filter, campaigns: Collapsed[], playlists: Collapsed[], media: Collapsed[], params: AccountParams): Promise<MediaPlaybackAggregate> => {
  // filter.cutoff (CX-638 §05) no llega aquí: getMediaViews delega en el
  // bundle vendorizado brooxCloudApi.js, cuyo parseFilter interno fuerza
  // endOf('day') sin ninguna forma de override. Arreglarlo exige que el
  // backend de la API cloud acepte un corte horario — fuera del alcance de
  // este repo.
  const mediaViews = await getMediaViews(filter, params.host, params.token || params.id, params.apiKey);
  const result: MediaPlaybackAggregate = { noViews: 0, fullViews: 0, partialViews: 0, mostViewedCampaign: { name: '', count: 0 }, list: [] };
  console.log('mediaViews', mediaViews);
  for(const mediaView of mediaViews) {
    const m = findById(media, mediaView.mediaId);
    const c = findById(campaigns, mediaView.campaignId);
    const p = findById(playlists, mediaView.playlistId);
    result.list.push({ id: mediaView.mediaId, name: m?.name || mediaView.mediaId, campaign: c?.name || 'N/A', playlist: p?.name || 'N/A', noViews: mediaView.noViews, fullViews: mediaView.fullViews, partialViews: mediaView.partialViews });
    result.fullViews += mediaView.fullViews;
    result.partialViews += mediaView.partialViews;
    result.noViews += mediaView.noViews;
    if(mediaView.partialViews + mediaView.fullViews >= result.mostViewedCampaign.count) {
      result.mostViewedCampaign = { name: c?.name || 'N/A', count: mediaView.partialViews + mediaView.fullViews };
    }
  }
  return result;
}

const getPreviousMediaPlayback = async (filter: Filter, campaigns: Collapsed[], playlists: Collapsed[], media: Collapsed[], params: AccountParams): Promise<MediaPlaybackAggregate> => {
  filter = FilterService.parse(filter);
  filter = FilterService.previous(filter);
  return await getMediaPlayback(filter, campaigns, playlists, media, params);
}

// Page size of the media playback list. The dashboard requests the first page to know
// whether to show the card, and the card reuses it.
const MEDIA_PAGE_SIZE = 21;
const MEDIA_PAGE_TTL = 60000;

const getMediaPlaybackPage = async (filter: Filter, page: number, pageSize: number, mediaName: string, params: AccountParams): Promise<MediaPlaybackList> => {
  return await cachedRequest(requestKey('mediaPlaybackPage', filter, params, page, pageSize, mediaName), MEDIA_PAGE_TTL, () => getMediaPlaybackApi(filter, page, pageSize, mediaName, params.host, params.token || params.id, params.apiKey));
}

const getMediaPlaybackTotals = async (filter: Filter, params: AccountParams): Promise<MediaPlaybackTotals> => {
  return await getMediaPlaybackTotalsApi(filter, params.host, params.token || params.id, params.apiKey);
}

const getPreviousMediaPlaybackTotals = async (filter: Filter, params: AccountParams): Promise<MediaPlaybackTotals> => {
  filter = FilterService.parse(filter);
  filter = FilterService.previous(filter);
  return await getMediaPlaybackTotals(filter, params);
}

export { MEDIA_PAGE_SIZE, MediaPlaybackAggregate, MediaPlayback, MediaPlaybackList, Views, MediaPlaybackTotals, getMediaPlayback, getPreviousMediaPlayback, getMediaPlaybackPage, getMediaPlaybackTotals, getPreviousMediaPlaybackTotals };
